import { Component, ChangeDetectorRef, inject, input } from '@angular/core';
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FlyoutType, MenuPanelItem } from '../../../domain/menu.domain';
import { IconComponent } from '../../../components/icon/icon.component';
import { MenuItemComponent } from '../item/item.component';

/**
 * The Menu Item Toggle Component
 *
 * This is the menu item toggle component for menu
 */
@Component({
  selector: 'td-wb-menu-item-toggle',
  templateUrl: './item-toggle.component.html',
  styleUrls: ['./item-toggle.component.scss'],
  imports: [
    IconComponent
  ]
})
export class MenuItemToggleComponent {
  private readonly menuItemComponent = inject(MenuItemComponent);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  /**
   * Icon Id
   */
  readonly iconId = input<string>(undefined);

  /**
   * Icon size
   */
  readonly size = input<number | string>(undefined);

  readonly flyoutType = input<FlyoutType>(undefined);

  get item(): MenuPanelItem { return this.menuItemComponent.menuItem(); }

  /**
   * The constructor of the component.
   */
  constructor() {
    outputToObservable(this.menuItemComponent.openChange)
      .pipe(takeUntilDestroyed())
      .subscribe((): void => this.changeDetectorRef.detectChanges());
    outputToObservable(this.menuItemComponent.activeChange)
      .pipe(takeUntilDestroyed())
      .subscribe((): void => this.changeDetectorRef.detectChanges());
  }

  /**
   * This function gets the href link of item
   *
   * @returns Returns href link of item.
   */
  getLink(): string {
    if (this.item.menu !== undefined && this.item.menu.length > 0) {
      return '';
    }
    return this.item.href;
  }

  /**
   * This function returns whether menu item is opened
   *
   * @returns Returns true if menu item is opened. Otherwise, false.
   */
  isOpen(): boolean {
    return this.menuItemComponent.isOpen();
  }

  /**
   * This function handles the click event of toggle
   *
   * @param event Click event
   */
  onClick(event: MouseEvent): void {
    if (this.getLink() === '' || this.getLink() === undefined) {
      this.menuItemComponent.toggle();
      event.preventDefault();
    } else {
      this.menuItemComponent.setOpen(true);
    }
  }

  /**
   * This function returns the classes for toggle button
   *
   * @returns Returns classes for toggle button.
   */
  getToggleClass(): string {
    const classes = [];
    classes.push('td-wb-nav__flyout-toggle');
    if (this.flyoutType() === 'primary') {
      classes.push('item-toggle-primary');
    } else {
      classes.push('item-toggle-secondary');
    }
    if (this.menuItemComponent.isActive()) {
      classes.push('item-toggle-active');
    }
    if (this.menuItemComponent.isOpen() && this.getLink() === '') {
      classes.push('item-toggle-open');
    }
    return classes.join(' ');
  }

  castToString(value: number | string): string {
    return String(value);
  }
}
